import { useEffect, useState } from 'react';
import { Platform } from 'react-native';
import { usePathname, useRouter } from 'expo-router';
import * as SplashScreen from 'expo-splash-screen';
import { useAuth } from '../contexts/AuthContext';
import {
  getStoreHydrated,
  subscribeStoreHydration,
  useItineraryStore,
} from '../store/itinerary-store';

/** Sticky across renders + remounts. Splash-hide and the cold-start
 *  resume-to-context decision are one-shot events per app launch;
 *  module-level flags (vs. useRef) make that intent explicit and
 *  survive any incidental SplashGate remount. */
let _splashHidden = false;
let _resumeDecided = false;

/**
 * Web counterpart to `SplashScreen.hideAsync()`. The native splash module is a
 * no-op on web, so we dismiss the server-rendered overlay from `+html.tsx`
 * here instead, at the same ready-gate. Fades via the CSS opacity transition,
 * then removes the node; a timeout backstops the case where `transitionend`
 * never fires (reduced motion sets `transition: none`).
 */
function hideWebSplash(): void {
  if (Platform.OS !== 'web' || typeof document === 'undefined') return;
  const el = document.getElementById('kiips-splash');
  if (!el) return;
  const remove = () => el.remove();
  el.addEventListener('transitionend', remove, { once: true });
  el.classList.add('kiips-splash--hidden');
  setTimeout(remove, 450);
}

/**
 * Holds the native splash screen open until three signals line up:
 *  1. Fonts loaded — gated upstream in `_layout.tsx` (returns null until
 *     fonts resolve, so this component doesn't mount before then).
 *  2. Auth resolved — `useAuth().loading === false` after the cold-start
 *     token check / refresh round-trip.
 *  3. Zustand persist rehydrated — so screens read the persisted store
 *     instead of the default empty state on first paint.
 *
 * Once all three are green, decides the resume-to-context redirect and only
 * then hides the splash:
 *  - Authenticated + an `activeTripId` still present locally
 *      → replace to `/trips/{activeTripId}`.
 *  - Unauthenticated + AsyncStorage held a token at boot
 *    (`hadPriorSession`) and we're sitting on the default `/` route
 *      → replace to `/login`. Covers the "session expired while the app
 *      was closed" reopen so the user isn't dropped onto the marketing
 *      page before a screen-level guard kicks them out.
 *  - Otherwise (anonymous first-run, or deep link to a specific route)
 *      → no redirect.
 *
 * The native `SplashScreen.hideAsync()` is deferred two frames after the
 * redirect dispatches so the new screen has a chance to commit before the
 * splash lifts — without that buffer the previous route can paint during
 * the splash fade-out.
 *
 * Must be rendered inside `AuthProvider`. See ADR-0068.
 */
export function SplashGate({ children }: { children: React.ReactNode }) {
  const { loading: authLoading, isAuthenticated, hadPriorSession } = useAuth();
  const router = useRouter();
  const pathname = usePathname();
  const [storeHydrated, setStoreHydrated] = useState<boolean>(() => getStoreHydrated());

  useEffect(() => {
    if (storeHydrated) return;
    return subscribeStoreHydration(() => setStoreHydrated(true));
  }, [storeHydrated]);

  useEffect(() => {
    if (_splashHidden) return;
    if (authLoading || !storeHydrated) return;
    if (!_resumeDecided) {
      _resumeDecided = true;
      if (isAuthenticated) {
        const { activeTripId, trips } = useItineraryStore.getState();
        if (activeTripId && trips.some((t) => t.id === activeTripId)) {
          router.replace(`/trips/${activeTripId}`);
        }
      } else if (hadPriorSession && pathname === '/') {
        // Pathname check skips this when Expo Router resolved a deep
        // link (e.g. /pricing, /trips/X, /join/...). Honouring the URL
        // is safer than overriding it — protected screens enforce auth
        // via their own guards, and unprotected ones (marketing,
        // /pricing, /privacy, etc.) are fine to view signed out.
        router.replace('/login');
      }
    }
    _splashHidden = true;
    // Two animation frames give React time to commit the `router.replace`
    // and mount the destination screen before the native splash starts
    // fading. Without this, the splash's fade-out can briefly reveal the
    // pre-redirect route. `setTimeout(0)` would queue a macrotask but
    // wouldn't wait for a paint; rAF aligns with the renderer.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        void SplashScreen.hideAsync();
        hideWebSplash();
      });
    });
  }, [authLoading, storeHydrated, isAuthenticated, hadPriorSession, pathname, router]);

  return children;
}
